import { useEffect } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const CANONICAL_BASE = "https://auvergnetechracing.com";

/**
 * Sets / updates a <meta> tag in <head>.
 */
const setMeta = (selector, attr, value) => {
  if (!value) return;
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    const match = selector.match(/\[([^=]+)="([^"]+)"\]/);
    if (match) tag.setAttribute(match[1], match[2]);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", value);
};

/**
 * Sets or updates the <link rel="canonical"> tag.
 */
const setCanonical = (href) => {
  if (!href) return;
  let tag = document.head.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", "canonical");
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
};

/**
 * Removes the <link rel="canonical"> tag (if any). Used on internal / noindex
 * routes so we do not accidentally canonicalize them to another page.
 */
const removeCanonical = () => {
  const tag = document.head.querySelector('link[rel="canonical"]');
  if (tag) tag.remove();
};

/**
 * useSiteMeta — Dynamically applies per-page SEO metadata.
 *
 * @param {object} [overrides] Optional per-page overrides. Any field passed here
 *   takes precedence over the values fetched from /api/content/site_meta.
 *   Accepted keys: title, description, keywords, og_title, og_description,
 *                  og_image, twitter_title, twitter_description, canonical_path.
 *
 * Usage:
 *   useSiteMeta();                                                  // Home page (uses global site_meta)
 *   useSiteMeta({ title: "...", description: "...", canonical_path: "/shop" });
 */
export const useSiteMeta = (overrides = {}) => {
  useEffect(() => {
    let cancelled = false;

    const apply = (m) => {
      if (cancelled) return;
      if (m.title) document.title = m.title;
      if (m.description) setMeta('meta[name="description"]', "name", m.description);
      if (m.keywords) setMeta('meta[name="keywords"]', "name", m.keywords);
      if (m.og_title) setMeta('meta[property="og:title"]', "property", m.og_title);
      if (m.og_description) setMeta('meta[property="og:description"]', "property", m.og_description);
      if (m.og_image) {
        setMeta('meta[property="og:image"]', "property", m.og_image);
        setMeta('meta[name="twitter:image"]', "name", m.og_image);
      }
      if (m.og_image_alt) {
        setMeta('meta[property="og:image:alt"]', "property", m.og_image_alt);
        setMeta('meta[name="twitter:image:alt"]', "name", m.og_image_alt);
      }
      if (m.twitter_title) setMeta('meta[name="twitter:title"]', "name", m.twitter_title);
      if (m.twitter_description) setMeta('meta[name="twitter:description"]', "name", m.twitter_description);

      if (m.noindex) {
        // Internal / non-SEO route — tell crawlers not to index and drop any stale
        // canonical from a previously-rendered public route.
        setMeta('meta[name="robots"]', "name", "noindex,nofollow");
        removeCanonical();
      } else {
        // Public route — restore indexable robots and set the canonical + og:url.
        setMeta('meta[name="robots"]', "name", "index, follow");
        const canonicalPath = m.canonical_path || "/";
        const canonicalUrl = CANONICAL_BASE + canonicalPath;
        setCanonical(canonicalUrl);
        setMeta('meta[property="og:url"]', "property", canonicalUrl);
      }
    };

    // 1. Apply overrides immediately for instant per-page updates
    if (Object.keys(overrides).length > 0) {
      apply({
        // fall through to og_* using base title/desc when not specified
        og_title: overrides.title,
        og_description: overrides.description,
        twitter_title: overrides.title,
        twitter_description: overrides.description,
        ...overrides,
      });
    }

    // 2. Fetch the CMS-managed site_meta and merge (overrides still win, and
    // og_*/twitter_* fall back to the per-page title/description when not explicit).
    axios
      .get(`${API}/api/content/site_meta`)
      .then((r) => {
        if (cancelled) return;
        const cms = r.data || {};
        const merged = {
          ...cms,
          // Derive OG/Twitter from the page-specific title/desc unless overrides pin them.
          og_title: overrides.og_title || overrides.title || cms.og_title,
          og_description: overrides.og_description || overrides.description || cms.og_description,
          twitter_title: overrides.twitter_title || overrides.title || cms.twitter_title,
          twitter_description: overrides.twitter_description || overrides.description || cms.twitter_description,
          ...overrides,
        };
        apply(merged);
      })
      .catch(() => {
        /* silent fallback to index.html defaults */
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(overrides)]);
};
